import React, { useState, useRef, useEffect } from 'react'
import { View, TouchableOpacity, Dimensions, Platform } from 'react-native'
import { Autocomplete, AutocompleteItem, Icon } from '@ui-kitten/components'
import * as Sentry from '@sentry/react-native'
import { searchClientsWithTelemetry } from '../utils/clientSearch'
import { getClientById } from '../global/Firebase/clients'
import { useTranslation } from 'react-i18next'

// Debounce so the checkout-path autocomplete doesn't fire a backend client-search
// request (and, on a miss, a full-org fuzzy scan) on every keystroke. Mirrors ClientsScreen.
function debounce(func, wait) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// `organizationId` is intentionally not destructured: since ATL-680 the backend
// resolves the org from the auth token, so the search call no longer needs it.
// Callers still pass it; it lands harmlessly in `...props`.
const ClientAutoComplete = React.memo(function ClientAutoComplete({onSelect, db, defaultValue=null, disabled=false, ...props}) {
  const { t } = useTranslation()
  const [value, setValue] = useState(defaultValue || '')
  const [hits, setHits] = useState([{key: 'searchHint', phoneNumber: '', displayName: t('searchHint')}])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = React.createRef()
  const _viewRef = useRef(null)

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue)
      inputRef.current?.focus()
    }
  }, [])

  const performSearch = async (query) => {
    if (!query || query.length < 2) {
      setHits([{key: 'searchHint', phoneNumber: '', displayName: t('searchHint')}])
      return
    }

    setIsSearching(true)
    try {
      const result = await searchClientsWithTelemetry(query, 10, { screen: 'NewSale' })
      if (result.status === 'error') {
        // A failed search must NOT look like "no such client" — the cashier would
        // wrongly conclude the client does not exist and ring the sale up without
        // them (losing the client's discount/bonuses). Surface it as a failure so
        // they retry rather than trust an empty list (ATL-742).
        setHits([{key: 'searchError', phoneNumber: '', displayName: t('clientSearchFailed')}])
        return
      }
      const normalizedResults = result.clients.map((client) => ({
        ...client,
        id: client.firestoreId || client.id,
        displayName: client.name,
        phoneNumber: client.phoneNumber || client.phone,
        querySource: 'supabase'
      }))
      setHits(normalizedResults)
    } catch (error) {
      // searchClientsWithTelemetry never throws, so this only catches an
      // unexpected local failure (e.g. normalising a malformed hit). It must
      // stay: nothing awaits this promise (debouncedSearch is fire-and-forget),
      // so letting it reject would trip the global unhandledrejection handler —
      // the ATL-562 flood class. Report it rather than swallowing it.
      Sentry.captureException(error, { tags: { client_search_error: true, screen: 'NewSale' } })
      setHits([{key: 'searchError', phoneNumber: '', displayName: t('clientSearchFailed')}])
    } finally {
      setIsSearching(false)
    }
  }

  // Keep a stable debounced wrapper that always calls the latest performSearch.
  const performSearchRef = useRef(performSearch)
  performSearchRef.current = performSearch
  const debouncedSearch = useRef(debounce((q) => performSearchRef.current(q), 300)).current

  const clearInput = () => {
    setValue('')
    setHits([{key: 'searchHint', phoneNumber: '', displayName: t('searchHint')}])
  }

  const renderCloseIcon = (props) => (
    <TouchableOpacity onPress={clearInput}>
      <Icon {...props} name='close'/>
    </TouchableOpacity>
  )

  const containerStyle = { width: '100%' }
  
  // Get appropriate width based on platform
  const getAutocompleteStyle = () => {
    const baseStyle = { width: '100%' }
    
    if (Platform.OS === 'web') {
      // For web, let the container handle the width naturally
      return [baseStyle, props.style]
    } else {
      // For mobile, use the original logic
      const width = _viewRef.current?.offsetWidth || (Dimensions.get('window').width - 40)
      return [{ width }, baseStyle, props.style]
    }
  }

  // Get appropriate width for AutocompleteItem based on platform
  const getItemWidth = () => {
    if (Platform.OS === 'web') {
      return '100%'
    } else {
      return _viewRef.current?.offsetWidth || (Dimensions.get('window').width - 40)
    }
  }
  
  return (
    <View ref={_viewRef} style={containerStyle}>
      <Autocomplete 
        ref={inputRef}
        placeholder={t('search')}
        value={value}
        onChangeText={(text) => {
          setValue(text)
          debouncedSearch(text)
        }}
        style={getAutocompleteStyle()}
        accessoryRight={renderCloseIcon}
        onSelect={async (index) => {
          let option = hits[index]
          // Placeholder rows (hint / failure notice) carry no client to select.
          if (option.key === 'searchHint' || option.key === 'searchError') {
            return
          }
          // Search is Supabase-backed (ATL-680) and returns a lightweight hit that
          // omits fields the sale flow needs (notably the nested `loyaltyDiscount`
          // object used to auto-apply the client's discount). Re-fetch the full
          // client from the active read source (Firestore by default) so the
          // selected client carries its complete data. Fall back to the raw hit
          // if the fetch fails so selection never breaks.
          try {
            const fullClient = await getClientById(db, option.id)
            onSelect(fullClient ? { ...option, ...fullClient, id: option.id } : option)
          } catch (error) {
            console.error('Failed to load full client after search select:', error)
            onSelect(option)
          }
          clearInput()
        }}
        disabled={disabled}
      >
        {isSearching ? (
          <AutocompleteItem key="searching" title={t('searching')} style={{ width: getItemWidth() }} />
        ) : (
          hits && hits.map((hit, index) => {
            return (
              <AutocompleteItem 
                key={hit.id || index} 
                title={hit.displayName + (hit.phoneNumber ? ', ' : '') + hit.phoneNumber}
                style={{ width: getItemWidth() }}
              />
            )
          })
        )}
      </Autocomplete>
    </View>
  )
}, function areEqual(_prevProps, _nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  return true
})

const SearchWrapper = React.memo(function SearchWrapper({ organizationId, db, style, autoCompleteStyle, onSelect, defaultValue=null, disabled=false}) {
  return (
    <View style={style}>
      <ClientAutoComplete disabled={disabled} db={db} organizationId={organizationId} style={autoCompleteStyle} onSelect={onSelect} defaultValue={defaultValue} />
    </View>
  )
}, function areEqual(_prevProps, _nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  return true
})

export default SearchWrapper
